import { HttpInterceptorFn, HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, switchMap } from 'rxjs/operators';
import { EMPTY, from, throwError } from 'rxjs';

interface PermissionDeniedPayload {
  code: string;
  message: string;
  companyName?: string;
  adminEmail?: string;
}
import { authService } from '../../../shared/services/auth/auth.service';
import { ToastService } from '../../../shared/services/toast/toast.service';
import { LoaderService } from '../../../shared/services/loader/loader.service';
import { CallerModuleService } from '../../services/caller-module.service';
import { UserPreferencesService } from '../../../shared/services/user-preferences/user-preferences.service';
import { environment } from '../../../environments/environment';
import { ENDPOINTS } from '../../../shared/constants/endpoints';
import { isWorkosAuth } from '../../config/auth-provider';

// Keep in sync with PUBLIC_ROUTES in the Node backend (womsRoutes.ts / defectRoutes.ts).
// Requests matching these METHOD+path pairs are passed through without auth — the server
// independently validates them via shared-secret headers.
const PUBLIC_API_ROUTES: ReadonlySet<string> = new Set([
  // Integration callbacks — authenticated by their own shared-secret headers.
  'POST /api/woms/jobs/callback',
  'POST /api/woms/update',
  'POST /api/defect/jobs/callback',
  'POST /api/defect/update',
]);

const BOOKING_TOKEN_HEADER = 'x-booking-token';

function isPublicApiRoute(method: string, url: string): boolean {
  try {
    const pathname = new URL(url, location.origin).pathname.toLowerCase();
    return PUBLIC_API_ROUTES.has(`${method.toUpperCase()} ${pathname}`);
  } catch {
    return false;
  }
}

// UserLoginV2 (ENDPOINTS.AUTH.LOGIN) is a login-time identity lookup, not a call against
// an established session. A 403 from it (deactivated account/company, unaccepted invite,
// not registered in this company — COL-724) is the backend's specific reason the login
// itself is being denied, not a permission failure on a resource within a session. The
// generic 403 handling below shows an unrelated toast and swallows the response before
// the login screen ever sees it, so this request is excluded from it.
//
// Derived from ENDPOINTS.AUTH.LOGIN + environment.nodeApiUrl (the same pieces
// auth.service.ts's login() builds its request URL from) and compared for exact
// pathname equality — not a suffix match, so an unrelated route that merely ends
// in the same segment (e.g. an admin proxy path) can never be mistaken for it.
const LOGIN_LOOKUP_PATH = (() => {
  try {
    return new URL(`${environment.nodeApiUrl}${ENDPOINTS.AUTH.LOGIN}`, location.origin).pathname.toLowerCase();
  } catch {
    return '';
  }
})();

function isLoginLookupRequest(url: string): boolean {
  if (!LOGIN_LOOKUP_PATH) return false;
  try {
    return new URL(url, location.origin).pathname.toLowerCase() === LOGIN_LOOKUP_PATH;
  } catch {
    return false;
  }
}

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const service = inject(authService);
  const toast = inject(ToastService);
  const loader = inject(LoaderService);
  const callerModule = inject(CallerModuleService);
  const userPreferences = inject(UserPreferencesService);

  // Read per-request, not once: the caller module changes with the route, and the company
  // (so the subdomain with it) changes on a company switch without a page reload.
  const contextHeaders = (): Record<string, string> => {
    const headers: Record<string, string> = {};
    const moduleName = callerModule.getCaller();
    if (moduleName) headers['X-Caller-Module'] = moduleName;
    // The companyURL the login response carried on the active company's userCompanyDtls row.
    // WorkOS (COL-786): the company is inside the signed token; the API ignores this header for
    // WorkOS tokens, so it is not sent at all.
    const subdomain = userPreferences.getCompanyURL();
    if (subdomain && !isWorkosAuth()) headers['x-subdomain'] = subdomain;
    return headers;
  };

  // EC1: Bypass Cognito endpoints entirely — checkTokenAndRefresh()
  // calls these URLs internally. Intercepting them would cause an
  // infinite loop: refresh → interceptor fires → refresh → ...
  // Note: these URLs don't match protectedUrls anyway (they don't
  // start with nodeApiUrl/notificationApiUrl) so this is a safety
  // net in case environment config ever changes.
  if (
    req.url.includes('oauth2/token') ||
    req.url.includes('userInfo') ||
    (environment.cognitoConfig.awsBaseURL && req.url.includes(environment.cognitoConfig.awsBaseURL))
  ) {
    return next(req);
  }

  const protectedUrls = [environment.nodeApiUrl, environment.notificationApiUrl].filter(
    (url): url is string => !!url
  );
  const requiresAuth = protectedUrls.some((url) => req.url.startsWith(url));

  // HttpHeaders.has() is case-insensitive, so this matches however the caller cased it.
  const isPublicBookingRequest = req.headers.has(BOOKING_TOKEN_HEADER);

  // Public booking requests carry no session, so the authenticated-user error toasts below
  // ("no permission", "record not found") don't apply — a 401/403 there means the link itself
  // is bad, and the booking page has its own message for that. Let those errors through to it.
  const showsAuthenticatedErrors = requiresAuth && !isPublicBookingRequest;

  // EC3: CRITICAL — keep authToken at top-level closure scope.
  // It is assigned AFTER proactive refresh completes (fresh value)
  // and read inside the nested reactive 401 catchError below.
  // Moving this inside switchMap would break the reactive 401 guard.
  let authToken: string | null = null;

  // EC2: Extract all non-401 error handling into shared function so
  // it applies to BOTH requiresAuth and non-requiresAuth paths.
  // status === 0 network error MUST be handled for all requests.
  const handleErrors = (error: HttpErrorResponse) => {
    const silentPaths = [
      // The dispatch board explains its own 403s ("isn't switched on for your company yet", "no
      // permission") and 404s ("That no longer exists", then reloads). The generic toasts would
      // duplicate them, and a swallowed 403 never reaches the board.
      '/api/dispatch-board/',
      '/api/notifications/unread/count',
      '/api/notifications',
      '/api/export/active-jobs',
      '/api/export/status/',
      // COL-765: the working-hours screens explain their own 403s (FEATURE_NOT_ENABLED: "switched
      // off", PERMISSION_DENIED) and 404s (not a technician: the drawer quietly keeps its usual
      // view). The generic toasts would contradict them, and a swallowed 403 never reaches them.
      '/api/technician-availability/',
      // My Profile asks where the person's day starts to learn whether the planner is on: 404
      // PLANNER_OFF just hides that section, and the section says its own problems. The generic
      // "Record not found." would show on every visit at a company without the planner.
      '/api/my-plan/start',
      // Your company (My Profile): 403 means they aren't a subcontractor's admin, so the card hides;
      // the card says its own problems (an address the map can't find, a logo not saved).
      '/api/my-company',
    ];
    let isSilent = false;
    try {
      const p = new URL(req.url, location.origin).pathname;
      isSilent = silentPaths.some(sp =>
        p === sp || p.startsWith(sp.endsWith('/') ? sp : `${sp}/`)
      );
    } catch { /* keep isSilent = false */ }

    // Applies to ALL requests — requiresAuth or not
    if (error.status === 0 && !isSilent) {
      loader.hideLoader();
      toast.showError('Network error — unable to reach the server. Please check your connection.');
      return throwError(() => error);
    }

    if (error.status === 403 && isLoginLookupRequest(req.url)) {
      loader.hideLoader();
      return throwError(() => error);
    }

    // 28 Oct 2026: FEATURE_NOT_ENABLED means the feature is switched off for this company, not that
    // the person lacks permission. No toast; the error goes to the caller so it can react (the
    // Schedules button hides itself, instead of every list showing a false "no permission").
    if (error.status === 403 && (error.error as PermissionDeniedPayload | null)?.code === 'FEATURE_NOT_ENABLED') {
      loader.hideLoader();
      return throwError(() => error);
    }

    if (error.status === 403 && showsAuthenticatedErrors && !isSilent) {
      loader.hideLoader();
      const payload = error.error as PermissionDeniedPayload | null;
      if (payload?.code === 'PERMISSION_DENIED' && typeof payload.message === 'string') {
        toast.showError(payload.message);
      } else {
        toast.showError("You don't have permission to perform this action.");
      }
      return EMPTY;
    }

    if (error.status === 404 && showsAuthenticatedErrors && !isSilent) {
      loader.hideLoader();
      if (req.responseType !== 'blob') {
        toast.showError('Record not found.');
      }
      return throwError(() => error);
    }

    return throwError(() => error);
  };

  // Non-protected requests, public booking requests and public API routes — pass through with
  // shared error handling. All three are server-gated by a booking token or a shared secret,
  // so no Bearer token is required and no refresh/sign-out must be triggered on their behalf.
  //
  // A public booking request still carries the Bearer when one is already in storage. Some of these
  // endpoints accept either credential (`dualAuth` on the Node side) and resolve the company from
  // whichever they get, so a signed-in staff member previewing the customer journey should be
  // recognised as themselves rather than as an anonymous scanner. It is read from storage and
  // attached as-is — deliberately no `checkTokenAndRefresh()`, because that path calls `signOut()`
  // when there is no session, which would redirect a real customer out of their registration.
  //
  // Server precedence, verified against api_node — the booking token always wins, so a stale staff
  // token left behind in a shared/kiosk browser cannot misattribute a customer's submission:
  //   - bookingTokenAuth.ts reads `x-booking-token` BEFORE staff auth (pinned by a COL-512
  //     regression test: a valid booking token beats an also-valid staff Bearer).
  //   - `dualAuth` (eoi.middleware.ts) verifies the capability token first and calls next()
  //     immediately; the staff-Bearer path runs only when that token is absent or malformed.
  //   - POST/GET /api/v1/eoi/eois skip the global authenticateJWT entirely
  //     (skipAuthForPublicRoutes in index.ts), so `req.user` is never populated on the public
  //     submit and buildActingUser falls back to capabilityToken.customerID — `createdBy` is
  //     the customer, not whichever staff member last signed in on that browser.
  // An endpoint added to this path must preserve that ordering; without it, attaching the Bearer
  // here stops being safe.
  if (!requiresAuth || isPublicBookingRequest || isPublicApiRoute(req.method, req.url)) {
    if (isPublicBookingRequest && !req.headers.has('Authorization')) {
      const staffToken = service.getTokenFromStorage()?.access_token ?? null;
      if (staffToken) {
        return next(req.clone({ setHeaders: { Authorization: `Bearer ${staffToken}` } })).pipe(
          catchError(handleErrors)
        );
      }
    }
    return next(req).pipe(catchError(handleErrors));
  }

  // Protected requests — proactive token refresh BEFORE sending.
  //
  // Fast path: token valid → checkTokenAndRefresh() returns true
  //   instantly, zero Cognito calls, negligible overhead.
  // Slow path: token expired → one Cognito call, new token stored,
  //   request fires with fresh token → no 401.
  //
  // EC4: refreshInFlight singleton in checkTokenAndRefresh() ensures
  // concurrent requests (background polls + user actions firing
  // simultaneously) share ONE Cognito call — not N calls.
  //
  // EC7: .catch(() => false) on checkTokenAndRefresh() so a thrown
  // error inside it is treated as false rather than bubbling up to
  // the wrong catchError handler.
  return from(service.checkTokenAndRefresh().catch(() => false)).pipe(
    switchMap((refreshed) => {
      if (!refreshed) {
        // EC5: Guard signOut() against /auth routes — prevents a
        // redirect loop if a request fires during the login page
        // transition (e.g. Cognito callback in-flight).
        if (!window.location.pathname.startsWith('/auth')) {
          service.signOut();
        }
        // EC6: Use HttpErrorResponse (not plain Error) so component
        // error handlers that check error.status receive a proper 401.
        return throwError(() => new HttpErrorResponse({
          status: 401,
          statusText: 'Session expired — token refresh failed'
        }));
      }

      // EC3: Read FRESH token from storage AFTER refresh completes
      // and assign to top-level closure var so the reactive 401
      // guard below reads the correct post-refresh value.
      authToken = service.getTokenFromStorage()?.access_token ?? null;
      const headers: Record<string, string> = {
        ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
        ...contextHeaders(),
      };
      const modifiedRequest = req.clone({ setHeaders: headers });

      return next(modifiedRequest).pipe(
        catchError((error: HttpErrorResponse) => {
          if (error.status === 401) {
            // EC3: authToken was set from fresh storage above.
            // If still null there is no token to refresh — bail.
            // This preserves the original post-logout guard:
            // no token = user not logged in = don't retry.
            if (!authToken) return throwError(() => error);

            console.warn('First 401 detected. Checking token and retrying if valid.');

            // EC7: .catch(() => false) on retry refresh too
            return from(service.checkTokenAndRefresh().catch(() => false)).pipe(
              switchMap((isTokenValid) => {
                if (isTokenValid) {
                  const refreshedToken =
                    service.getTokenFromStorage()?.access_token ?? null;
                  const retryHeaders: Record<string, string> = {
                    ...(refreshedToken ? { Authorization: `Bearer ${refreshedToken}` } : {}),
                    ...contextHeaders(),
                  };
                  const retriedRequest = req.clone({ setHeaders: retryHeaders });
                  return next(retriedRequest).pipe(
                    catchError((secondError: HttpErrorResponse) => {
                      if (secondError.status === 401) {
                        console.error('Second 401 detected. Logging out.');
                        service.signOut();
                      }
                      return throwError(() => secondError);
                    })
                  );
                } else {
                  console.error('Token refresh failed. Logging out.');
                  service.signOut();
                  return throwError(() => error);
                }
              })
            );
          }
          // All other errors — shared handler
          return handleErrors(error);
        })
      );
    })
  );
};